import { useCallback, useEffect, useRef, useState } from 'react';

import DocumentMfaModal from '../../components/DocumentMfaModal';
import { documentMfaStore } from '../api/documentMfaStore';

interface IProps {
  children: React.ReactElement;
}

interface IPendingPrompt {
  resolve: () => void;
  reject: (reason?: unknown) => void;
}

/**
 * Mounts the document-download MFA modal at the application root and
 * registers a "prompter" with documentMfaStore. When secureDocumentDownload
 * encounters a 401 step_up_required, it calls documentMfaStore.requestStepUp
 * which is wired here to display the modal and await user verification.
 */
const DocumentMfaProvider = ({ children }: IProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const pendingRef = useRef<IPendingPrompt | null>(null);

  const promptStepUp = useCallback((): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      setOpen(true);
    });
  }, []);

  useEffect(() => {
    documentMfaStore.setPrompter(promptStepUp);
    return () => documentMfaStore.setPrompter(null);
  }, [promptStepUp]);

  const handleSuccess = () => {
    setOpen(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.resolve();
  };

  const handleCancel = () => {
    setOpen(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.reject(new Error('Document download MFA cancelled by user.'));
  };

  return (
    <>
      {children}
      <DocumentMfaModal open={open} onSuccess={handleSuccess} onCancel={handleCancel} />
    </>
  );
};

export default DocumentMfaProvider;
